import type { PunchStats } from './types';

export type PunchPhase = 'idle' | 'windup' | 'active' | 'recovery';

/** One punch's timeline: windup, the moment it lands, then recovery before the next one can start. */
export class Punch {
  private elapsed = 0;
  phase: PunchPhase = 'idle';

  constructor(public stats: PunchStats, private readonly landed: () => void) {}

  /** 0..1 through the current phase; 0 while idle. */
  get phaseProgress(): number {
    return this.phase === 'idle' ? 0 : Math.min(1, this.elapsed / this.phaseDuration);
  }

  /** How far the fist is out: 0 in the guard, 1 at full reach. */
  get extension(): number {
    switch (this.phase) {
      case 'windup':
        return this.phaseProgress;
      case 'active':
        return 1;
      case 'recovery':
        return 1 - this.phaseProgress;
      default:
        return 0;
    }
  }

  tryStart(): boolean {
    if (this.phase !== 'idle') return false;
    this.phase = 'windup';
    this.elapsed = 0;
    return true;
  }

  /** Stops the punch where it is; it will not land. */
  cancel(): void {
    this.phase = 'idle';
    this.elapsed = 0;
  }

  tick(seconds: number): void {
    if (this.phase === 'idle') return;
    this.elapsed += seconds;
    // A long step can cover several phases; walk through each so the punch still lands exactly once.
    while (!this.isIdle() && this.elapsed >= this.phaseDuration) {
      this.elapsed -= this.phaseDuration;
      this.advance();
    }
  }

  // A method, not a comparison inline: advancing changes the phase inside the loop.
  private isIdle(): boolean {
    return this.phase === 'idle';
  }

  private get phaseDuration(): number {
    switch (this.phase) {
      case 'windup':
        return this.stats.windupSeconds;
      case 'active':
        return this.stats.activeSeconds;
      case 'recovery':
        return this.stats.recoverySeconds;
      default:
        return 0;
    }
  }

  private advance(): void {
    switch (this.phase) {
      case 'windup':
        this.phase = 'active';
        this.landed();
        break;
      case 'active':
        this.phase = 'recovery';
        break;
      case 'recovery':
        this.phase = 'idle';
        this.elapsed = 0;
        break;
    }
  }
}
